import type { Alistamiento, CotizacionDeAlistamiento } from '@/lib/supabase/tipos-bd'
import { fechaHora } from '@/shared/utilidades/formato'

import estilos from './PaginaAlistamientos.module.css'

/**
 * LA LISTA DE ÓRDENES, A LA IZQUIERDA.
 *
 * Una tarjeta por orden de servicio: número, cliente, quién va y cuándo, y
 * un botón por cotización con lo que tiene en bodega («14/16»). El color dice
 * lo que importa de un vistazo: verde si está todo, ámbar si falta algo,
 * azul si todavía espera al cliente, morado si ya pasó a FEV.
 *
 * QUE PAREZCAN BOTONES (Johan, en el celular: «esto no parecen botones…, y
 * pasa lo mismo con FEV»). Eran filas planas y fichas planas: nada decía que
 * se podían tocar. Ahora la orden es una tarjeta con su flecha, y cada
 * cotización es un botón con borde y flecha que abre DIRECTAMENTE esa
 * cotización, sin pasar por la primera.
 */
export function ListaDeOrdenes({
  ordenes,
  elegida,
  onElegir,
}: {
  ordenes: Alistamiento[]
  elegida: string | null
  /** La orden, y si se tocó una cotización en particular, cuál. */
  onElegir: (id: string, cotizacionId?: string) => void
}) {
  return (
    <ul className={estilos.lista} aria-label="Órdenes con material">
      {ordenes.map((o) => {
        const viva = elegida === o.orden_servicio_id
        return (
          <li
            key={o.orden_servicio_id}
            className={`${estilos.fila} ${viva ? estilos.filaViva : ''}`}
          >
            <button
              type="button"
              className={estilos.filaBoton}
              onClick={() => onElegir(o.orden_servicio_id)}
              aria-current={viva}
            >
              <span className={estilos.filaCuerpo}>
                <span className={estilos.filaTope}>
                  <span className={estilos.filaNumero}>{o.numero}</span>
                  <span className={estilos.filaCliente}>{o.cliente ?? 'Sin cliente'}</span>
                </span>
                <span className={estilos.filaMeta}>
                  {o.tecnico ? o.tecnico.split(' ').slice(0, 2).join(' ') : 'Sin técnico'}
                  {o.programada && ` · ${fechaHora(o.programada)}`}
                </span>
              </span>
              <span className={estilos.filaFlecha} aria-hidden="true">
                ›
              </span>
            </button>
            <span className={estilos.fichas}>
              {o.cotizaciones.map((c) => (
                <Ficha
                  key={c.cotizacion_id}
                  c={c}
                  onAbrir={() => onElegir(o.orden_servicio_id, c.cotizacion_id)}
                />
              ))}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function Ficha({ c, onAbrir }: { c: CotizacionDeAlistamiento; onAbrir: () => void }) {
  // Ya en FEV (0252/0257): la ficha dice en qué va, no lo que hay en bodega.
  if (c.fev || c.facturada) {
    return (
      <button
        type="button"
        className={`${estilos.ficha} ${
          c.fev?.estado === 'error' ? estilos.fichaFalta : estilos.fichaFev
        }`}
        onClick={onAbrir}
      >
        {c.numero}
        <b>
          {c.fev?.documento === 'remision'
            ? c.fev.factura?.estado === 'facturada'
              ? c.fev.factura.numero ?? 'FEV'
              : c.fev.estado === 'remitida'
              ? c.fev.numero ?? 'REM'
              : c.fev.estado === 'error'
              ? 'error'
              : c.fev.estado === 'en_espera'
              ? 'REM en espera'
              : 'REM en cola'
            : c.fev?.estado === 'sin_iva'
            ? 'sin IVA'
            : c.fev?.estado === 'facturada' || c.facturada
            ? c.fev?.numero ?? 'FEV'
            : c.fev?.estado === 'error'
            ? 'error'
            : 'en cola'}
        </b>
        <span className={estilos.fichaFlecha} aria-hidden="true">
          ›
        </span>
      </button>
    )
  }
  const total = c.renglones.length
  const hay = c.renglones.filter((r) => Number(r.falta) <= 0).length
  const tono =
    c.estado !== 'aprobada'
      ? estilos.fichaEspera
      : hay === total
      ? estilos.fichaCompleta
      : estilos.fichaFalta
  return (
    <button type="button" className={`${estilos.ficha} ${tono}`} onClick={onAbrir}>
      {c.numero}
      {total > 0 ? (
        <b>
          {hay}/{total}
        </b>
      ) : (
        // Solo servicio: nada que alistar; falta pasarla a FEV.
        <b>sin material</b>
      )}
      <span className={estilos.fichaFlecha} aria-hidden="true">
        ›
      </span>
    </button>
  )
}
